import { Grid } from "@mui/material";
import { Dayjs } from "dayjs";

import { MonthCell } from "./MonthCell";

interface IMonthRowProps {
  value?: [Dayjs | null, Dayjs | null];
  months?: Dayjs[];
  minDate?: Dayjs;
  maxDate?: Dayjs;
  hoveredMonth: Dayjs | null;
  onHoverMonth?: (month: Dayjs) => void;
  onLeaveMonth?: () => void;
  onSelect?: (month: Dayjs) => void;
}

export const MonthRow = ({
  months,
  minDate,
  maxDate,
  value = [null, null],
  hoveredMonth,
  onSelect,
  onHoverMonth,
  onLeaveMonth,
}: IMonthRowProps) => {
  const [start, end] = value;

  const isRowInRange = (
    rowMonths: Dayjs[],
    start: Dayjs | null,
    end: Dayjs | null,
    hoveredMonth: Dayjs | null,
  ) => {
    if (!rowMonths.length || (!start && !hoveredMonth)) {
      return { isInRange: false, isFullyInRange: false };
    }

    const rowStart = rowMonths[0];
    const rowEnd = rowMonths[rowMonths.length - 1];
    const effectiveEnd =
      end ||
      (start && hoveredMonth && hoveredMonth.isAfter(start, "month")
        ? hoveredMonth
        : null);

    if (!start || !effectiveEnd) {
      return { isInRange: false, isFullyInRange: false };
    }

    const isInRange = rowMonths.some(
      (month) =>
        month.isSame(start, "month") ||
        month.isSame(effectiveEnd, "month") ||
        (month.isAfter(start, "month") &&
          month.isBefore(effectiveEnd, "month")),
    );
    const isFullyInRange =
      isInRange &&
      rowStart.isAfter(start, "month") &&
      rowEnd.isBefore(effectiveEnd, "month");

    return { isInRange, isFullyInRange };
  };

  const { isInRange, isFullyInRange } = isRowInRange(
    months ?? [],
    start,
    end,
    hoveredMonth,
  );

  return (
    <Grid container item xs={12} sx={{ px: 1, width: "100%" }}>
      {(months ?? []).map((month, index) => {
        const isBeforeMin = minDate && month.isBefore(minDate, "month");
        const isAfterMax = maxDate && month.isAfter(maxDate, "month");
        const disabled = Boolean(isBeforeMin || isAfterMax);
        const selected = Boolean(
          (start && month.isSame(start, "month")) ||
          (end && month.isSame(end, "month")),
        );

        const inPreview =
          start && !end && hoveredMonth && hoveredMonth.isAfter(start, "month")
            ? (month.isAfter(start, "month") &&
                month.isBefore(hoveredMonth, "month")) ||
              month.isSame(start, "month") ||
              month.isSame(hoveredMonth, "month")
            : false;

        const isPreviewStart = Boolean(
          start && hoveredMonth && month.isSame(start, "month"),
        );
        const isPreviewEnd = Boolean(
          start && hoveredMonth && month.isSame(hoveredMonth, "month"),
        );

        const isFirstCell = index === 0;
        const isLastCell = index === (months ?? []).length - 1;
        const roundStart = isInRange && isFirstCell;
        const roundEnd = isFullyInRange && isLastCell;

        const inSelectedRange = Boolean(
          start &&
          end &&
          month.isAfter(start, "month") &&
          month.isBefore(end, "month"),
        );

        return (
          <MonthCell
            key={month.format("YYYY-MM")}
            disabled={disabled}
            inPreview={inPreview}
            inSelectedRange={inSelectedRange}
            isPreviewEnd={isPreviewEnd}
            isPreviewStart={isPreviewStart}
            monthName={month.format("MMM")}
            rawMonth={month}
            roundEnd={roundEnd}
            roundStart={roundStart}
            selected={selected}
            onClick={onSelect}
            onMouseEnter={() => onHoverMonth?.(month)}
            onMouseLeave={() => onLeaveMonth?.()}
          />
        );
      })}
    </Grid>
  );
};
